import { useState, useEffect, useRef } from "react";
import { Layout } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import Sidebar from "../main-layout/Sidebar";
import { Navigate } from "react-router-dom";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { SelfUserClassificationEnum } from "../../shared/constants/constants";
import DashboardHeader from "../../components/Header/admin_header";
import MainLayout from "../main-layout/MainLayout";
import { useMenu } from "../../store/context/admin/menu-context";
import { useTheme } from "../../store/context/theme-context";
import {
  readPermissionFlagsFromCache,
  loadPermissionFlags,
} from "../../utils/helper/permission-toggle";

const { Content, Sider } = Layout;

const AdminLayout = ({ children }) => {
  const sessionData = getSessionData();
  const { theme } = useTheme();
  const { useV2Design, fetchMenuSchemas, isFetchingMenuSchema, menuList } =
    useMenu();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const isAuthenticated = !!sessionData;
  const isAdmin =
    sessionData?.userClassification ===
    SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;

  // Determine if sidebar should be hidden completely
  const shouldHideSidebar = useV2Design && isFetchingMenuSchema && !menuList;

  // Track if we've already attempted to load permissions and menu
  const hasInitializedRef = useRef(false);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  useEffect(() => {
    // Only run once when component mounts and conditions are met
    if (hasInitializedRef.current || !useV2Design || menuList) {
      return;
    }

    const fetchMenuWithPermissions = async () => {
      hasInitializedRef.current = true;

      // Ensure permissions are loaded before fetching menu schema
      // Note: Permissions are already loaded in OTP.js after login, so we only
      // need to check cache here. If cache exists, use it. If not, it means
      // this is a direct navigation/refresh, so load permissions.
      const cachedPermissions = readPermissionFlagsFromCache();
      console.log("cachedPermissions", cachedPermissions);
      if (!cachedPermissions) {
        // Only load if no cache exists (e.g., direct navigation or page refresh)
        // This prevents double loading after login since OTP.js already loads permissions
        try {
          await loadPermissionFlags({ forceRefresh: false });
          console.log("Permission flags loaded before fetching menu schema");
        } catch (error) {
          console.error("Error loading permission flags:", error);
          // Continue with menu fetch even if permission loading fails
        }
      }
      fetchMenuSchemas();
    };

    fetchMenuWithPermissions();
  }, [menuList, useV2Design, fetchMenuSchemas]);

  useEffect(() => {
    const handleResize = () => {
      const small = window.innerWidth < 768;
      setIsSmallScreen(small);
      setCollapsed(small);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/admin-login" replace />;
  }

  if (!isAdmin) {
    console.log("not an admin, redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <DashboardHeader collapsed={collapsed} onMenuClick={toggleCollapsed} />

      <Layout hasSider>
        {!shouldHideSidebar && (
          <Sider
            collapsed={collapsed}
            onCollapse={setCollapsed}
            width={270}
            collapsedWidth={70}
            style={{
              overflow: "hidden",
              height: "calc(100vh - 48px)",
              position: "fixed",
              left: 0,
              top: 48,
              bottom: 0,
              zIndex: 100,
              transition: "left 0.3s ease",
            }}
            breakpoint="md"
            onBreakpoint={setCollapsed}
          >
            {isAdmin && useV2Design ? (
              <MainLayout
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                type="inner"
              />
            ) : (
              <Sidebar collapsed={collapsed} type="inner" />
            )}
          </Sider>
        )}

        {collapsed && isSmallScreen && !shouldHideSidebar && (
          <MenuOutlined
            style={{
              position: "fixed",
              top: 80,
              left: 20,
              zIndex: 120,
              cursor: "pointer",
              color: "#1890ff",
              fontSize: "18px",
              backgroundColor: "white",
              padding: "8px",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
            onClick={toggleCollapsed}
          />
        )}

        {!collapsed && isSmallScreen && !shouldHideSidebar && (
          <div
            style={{
              position: "fixed",
              top: 48,
              left: 0,
              width: "100%",
              height: "calc(100% - 48px)",
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 90,
            }}
            onClick={toggleCollapsed}
          />
        )}

        <Layout
          style={{
            marginLeft: shouldHideSidebar
              ? 0
              : collapsed
              ? 70
              : isSmallScreen
              ? 0
              : 270,
            transition: "margin-left 0.2s ease",
            minHeight: "calc(100vh - 48px)",
          }}
        >
          <Content
            style={{
              overflow: "auto",
              padding: "24px",
              marginTop: "24px",
              backgroundColor: theme === "dark" ? "#111111" : "#fdfdfd",
            }}
          >
            <div className="min-h-[100vh] h-auto">{children}</div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
